import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy } from '@fortawesome/free-solid-svg-icons';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import './PlayerGeoRoundResult.css';
import { fetchCurrentGeoRoundResult } from '../../apiservices/axiosservice/subscriberservice';
import { mockGeoResult } from '../../data/mockdata';
import { useSse } from '../../sse';

const podiumColors = ['#8B8B8B', '#D4A843', '#8B7355'];

const targetMarker = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const playerMarker = new L.Icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const FitBounds: React.FC<{ points: [number, number][] }> = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length < 2) return;
    const bounds = L.latLngBounds(points);
    if (!bounds.isValid()) return;

    const tryFit = () => {
      try {
        map.fitBounds(bounds, { padding: [50, 50] });
      } catch {
        requestAnimationFrame(() => {
          try {
            map.fitBounds(bounds, { padding: [50, 50] });
          } catch {}
        });
      }
    };

    if ((map as any)._loaded) {
      requestAnimationFrame(tryFit);
    } else {
      map.whenReady(() => requestAnimationFrame(tryFit));
    }
  }, [points, map]);
  return null;
};

interface Props {
  onNextRound: () => void;
  showNextRound?: boolean;
  data?: GeoResult;
  leaderBoard: LeaderboardEntry[];
  gameInfo: GameStatusInfo;
}

const PLAYER_COLORS = [
  '#FF6B6B',
  '#4ECDC4',
  '#AB47BC',
  '#66BB6A',
  '#F9A825',
  '#42A5F5',
  '#FF7043',
];
const PLAYER_ICONS = ['🦊', '🐋', '🚀', '🌿', '🦁', '🎩', '🌸'];

const PlayerGeoRoundResult: React.FC<Props> = ({
  onNextRound,
  showNextRound = false,
  data,
  leaderBoard = [],
  gameInfo,
}) => {
  const [geoResultData, setGeoResultData] = useState<GeoResult>(mockGeoResult);
  const geoSync = useSse<any>('geo-sync');
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMapReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    console.log('Player Result Data:', data);
    if (data) {
      setGeoResultData(data);
    }
  }, []);

  const top3 = leaderBoard
    .slice(0, 3)
    .sort((a, b) => b.totalPoints - a.totalPoints);
  const podiumOrder = top3.length >= 3 ? [top3[1], top3[0], top3[2]] : top3;

  const allPoints: [number, number][] = [
    [
      geoResultData.targetLocation.latitude,
      geoResultData.targetLocation.longitude,
    ],
    ...geoResultData.pinnedGeoLocations.map(
      (p) =>
        [p.guessedLocation.latitude, p.guessedLocation.longitude] as [
          number,
          number,
        ]
    ),
  ];

  return (
    <div className="grr">
      {/* Left: Map */}
      <div className="grr__left">
        <div className="grr__header">
          <h1 className="grr__title">
            <FontAwesomeIcon icon={faTrophy} /> Round{' '}
            {geoResultData.currentRound} Over!
          </h1>
          <p className="grr__subtitle">
            {data?.pinnedGeoLocations.length} players
          </p>
        </div>
        <div className="grr__map">
          {mapReady && (
            <MapContainer
              center={[
                geoResultData.targetLocation.latitude,
                geoResultData.targetLocation.longitude,
              ]}
              zoom={8}
              style={{ height: '100%', width: '100%' }}
              zoomControl={false}
            >
              <TileLayer
                url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                noWrap
              />
              <FitBounds points={allPoints} />

              <Marker
                position={[
                  geoResultData.targetLocation.latitude,
                  geoResultData.targetLocation.longitude,
                ]}
                icon={targetMarker}
              >
                <Popup>{geoResultData.targetLocation.cityName}</Popup>
              </Marker>

              {geoResultData.pinnedGeoLocations.map((p) => (
                <Marker
                  key={p.playerId}
                  position={[
                    p.guessedLocation.latitude,
                    p.guessedLocation.longitude,
                  ]}
                  icon={playerMarker}
                >
                  <Popup>{p.playerName}</Popup>
                </Marker>
              ))}
              {geoResultData.pinnedGeoLocations.map((p, i) => (
                <Polyline
                  key={`line-${p.playerId}`}
                  positions={[
                    [p.guessedLocation.latitude, p.guessedLocation.longitude],
                    [
                      geoResultData.targetLocation.latitude,
                      geoResultData.targetLocation.longitude,
                    ],
                  ]}
                  pathOptions={{
                    color: PLAYER_COLORS[i % PLAYER_COLORS.length],
                    weight: 2,
                    dashArray: '5,10',
                  }}
                />
              ))}
            </MapContainer>
          )}
        </div>
      </div>

      {/* Right: Leaderboard */}
      <div className="grr__right">
        <div className="grr__lb-header">
          <span>🏆</span>
          <h3 className="grr__lb-title">Leaderboard</h3>
          <span className="grr__lb-round">
            After {gameInfo.currentRound} of {gameInfo.totalRound} rounds
          </span>
        </div>

        <div className="grr__podium">
          {podiumOrder.map((p, i) => {
            const place = podiumOrder.length >= 3 ? [2, 1, 3][i] : i + 1;
            return (
              <div key={p.playerId} className="grr__podium-col">
                <span
                  className="grr__podium-avatar"
                  style={{
                    background: p.iconColor,
                    borderColor:
                      place === 1 ? 'var(--color-primary)' : p.iconColor,
                  }}
                >
                  {p.playerIcon}
                </span>
                <span className="grr__podium-name">{p.playerName}</span>
                <div
                  className={`grr__podium-block grr__podium-block--${place}`}
                  style={{ background: podiumColors[i] }}
                >
                  <span className="grr__podium-place">{place}</span>
                  <span className="grr__podium-score">{p.totalPoints}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grr__location-card">
          <div className="grr__location-header">
            <span className="grr__location-pin">📍</span>
            <div>
              <h4 className="grr__location-city">
                {geoResultData.targetLocation.cityName}
              </h4>
              <p className="grr__location-coords">
                {geoResultData.targetLocation.latitude.toFixed(4)}° N,{' '}
                {geoResultData.targetLocation.longitude.toFixed(4)}° E
              </p>
            </div>
          </div>
        </div>

        <div className="grr__rankings">
          {leaderBoard.map((p, i) => (
            <div key={p.playerId} className="grr__rank-row">
              <span className="grr__rank-num">#{i + 1}</span>
              <span
                className="grr__rank-avatar"
                style={{ background: PLAYER_COLORS[i % PLAYER_COLORS.length] }}
              >
                {PLAYER_ICONS[i % PLAYER_ICONS.length]}
              </span>
              <div className="grr__rank-info">
                <span className="grr__rank-name">{p.playerName}</span>
                <span className="grr__rank-pts">{p.totalPoints} pts</span>
              </div>
            </div>
          ))}
        </div>

        {showNextRound && !gameInfo.scheduledGame && (
          <button className="grr__btn grr__btn--primary" onClick={onNextRound}>
            Start Next Round
          </button>
        )}
      </div>
    </div>
  );
};

export default PlayerGeoRoundResult;
